import type { Command } from './commands';
import type { EditorState, ID, ProductViewId, ViewportState } from '../domain/types';

type Listener = () => void;

interface HistoryEntry {
  command: Command<EditorState>;
  stateAfter: EditorState;
}

export interface StoreSnapshot {
  state: EditorState;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string;
  redoLabel?: string;
}

export class DesignStore {
  private state: EditorState;
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private listeners = new Set<Listener>();
  private snapshot: StoreSnapshot;
  private readonly historyLimit: number;

  constructor(initialState: EditorState, historyLimit = 100) {
    this.state = structuredClone(initialState);
    this.historyLimit = historyLimit;
    this.snapshot = this.makeSnapshot();
  }

  getState = (): EditorState => this.state;
  getSnapshot = (): StoreSnapshot => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  dispatch(command: Command<EditorState>): void {
    this.state = command.execute(this.state);
    this.undoStack.push({ command, stateAfter: structuredClone(this.state) });
    if (this.undoStack.length > this.historyLimit) this.undoStack.shift();
    this.redoStack = [];
    this.emit();
  }

  undo = (): void => {
    const entry = this.undoStack.pop();
    if (!entry) return;
    this.state = entry.command.undo(this.state);
    this.redoStack.push(entry);
    this.emit();
  };

  redo = (): void => {
    const entry = this.redoStack.pop();
    if (!entry) return;
    this.state = entry.command.execute(this.state);
    this.undoStack.push(entry);
    this.emit();
  };

  select(ids: ID[]): void {
    this.state = { ...this.state, selectedObjectIds: [...ids] };
    this.emit();
  }

  setActiveView(viewId: ProductViewId): void {
    this.state = { ...this.state, activeViewId: viewId, selectedObjectIds: [] };
    this.emit();
  }

  setViewport(viewId: ProductViewId, viewport: ViewportState): void {
    this.state = {
      ...this.state,
      viewports: { ...this.state.viewports, [viewId]: structuredClone(viewport) },
    };
    this.emit();
  }

  resetView(viewId = this.state.activeViewId): void {
    this.setViewport(viewId, { zoom: 1, panPx: { x: 0, y: 0 } });
  }

  replaceState(next: EditorState, clearHistory = true): void {
    this.state = structuredClone(next);
    if (clearHistory) {
      this.undoStack = [];
      this.redoStack = [];
    }
    this.emit();
  }

  private makeSnapshot(): StoreSnapshot {
    return {
      state: this.state,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoLabel: this.undoStack.at(-1)?.command.label,
      redoLabel: this.redoStack.at(-1)?.command.label,
    };
  }

  private emit(): void {
    this.snapshot = this.makeSnapshot();
    this.listeners.forEach(listener => listener());
  }
}
